import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Switch, InputNumber, Tabs, ColorPicker, Upload, DatePicker, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import { formsApi } from '../../api/forms';
import dayjs from 'dayjs';

interface AdminCourseDto {
    id: string;
    slug: string;
    title: string;
    description: string;
    icon?: string;
    imageUrl?: string;
    color?: string;
    orderIndex: number;
    isActive: boolean;
    formDefinitionId?: string;
    registrationUrl?: string;
    startDate?: string;
    endDate?: string;
    price?: number;
    availableLanguages: string[];
    translations: { 
        languageCode: string; 
        title: string; 
        description: string;
        scheduleText?: string;
        targetGroupText?: string;
        locationText?: string;
        instructorText?: string;
    }[];
}

export const Courses: React.FC = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    const { data: forms } = useQuery({
        queryKey: ['adminFormsSelect'],
        queryFn: () => formsApi.getAdminForms({ pageNumber: 1, pageSize: 100 })
    });

    const { data: courses, isLoading } = useQuery({
        queryKey: ['adminCourses'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/courses');
            return response.data.data || [];
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/courses/${id}`);
        },
        onSuccess: () => {
            message.success('Course deleted successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminCourses'] });
        },
        onError: () => {
            message.error('Failed to delete course.');
        }
    });

    const uploadImageMutation = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('isPublic', 'true');
            formData.append('isActive', 'true');

            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: (data: any) => {
            message.success('Image uploaded successfully.');
            form.setFieldsValue({ imageUrl: `/api/media/download/${data.id || data.Id}` });
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || 'Image upload failed.');
        }
    });

    const handleImageUpload = (file: File) => {
        uploadImageMutation.mutate(file);
        return false;
    };

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = appLanguages.map(lang => {
                const title = values[`translations_${lang}_title`];
                const description = values[`translations_${lang}_description`];
                const scheduleText = values[`translations_${lang}_scheduleText`];
                const targetGroupText = values[`translations_${lang}_targetGroupText`];
                const locationText = values[`translations_${lang}_locationText`];
                const instructorText = values[`translations_${lang}_instructorText`];
                
                if (!title) return null;
                return { 
                    languageCode: lang, 
                    title, 
                    description,
                    scheduleText,
                    targetGroupText,
                    locationText,
                    instructorText
                };
            }).filter(Boolean);

            if (translations.length === 0) {
                throw new Error('At least one translation is required.');
            }

            const payload = {
                id: editingCourseId || undefined,
                slug: values.slug,
                icon: values.icon,
                imageUrl: values.imageUrl,
                color: typeof values.color === 'string' ? values.color : values.color?.toHexString(),
                orderIndex: values.orderIndex,
                isActive: values.isActive,
                registrationUrl: values.registrationUrl,
                startDate: values.dateRange ? values.dateRange[0]?.toISOString() : undefined,
                endDate: values.dateRange ? values.dateRange[1]?.toISOString() : undefined,
                price: values.price,
                formDefinitionId: values.formDefinitionId,
                translations
            };

            if (editingCourseId) {
                await apiClient.put(`/admin/courses/${editingCourseId}`, payload);
            } else {
                await apiClient.post('/admin/courses', payload);
            }
        },
        onSuccess: () => {
            message.success('Course saved successfully.');
            setIsModalVisible(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['adminCourses'] });
            queryClient.invalidateQueries({ queryKey: ['publicCourses'] }); // Invalidate public cache too
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Failed to save course.');
        }
    });

    const handleEdit = (record: AdminCourseDto) => {
        setEditingCourseId(record.id);
        const formValues: any = {
            slug: record.slug,
            icon: record.icon,
            imageUrl: record.imageUrl,
            color: record.color,
            orderIndex: record.orderIndex,
            isActive: record.isActive
        };

        appLanguages.forEach(lang => {
            const trans = record.translations?.find((t: any) => t.languageCode === lang);
            if (trans) {
                formValues[`translations_${lang}_title`] = trans.title;
                formValues[`translations_${lang}_description`] = trans.description;
                formValues[`translations_${lang}_scheduleText`] = trans.scheduleText;
                formValues[`translations_${lang}_targetGroupText`] = trans.targetGroupText;
                formValues[`translations_${lang}_locationText`] = trans.locationText;
                formValues[`translations_${lang}_instructorText`] = trans.instructorText;
            }
        });

        if (record.startDate && record.endDate) {
            formValues.dateRange = [dayjs(record.startDate), dayjs(record.endDate)];
        }
        
        formValues.price = record.price;
        formValues.registrationUrl = record.registrationUrl;
        formValues.formDefinitionId = record.formDefinitionId;

        form.setFieldsValue(formValues);
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        setEditingCourseId(null);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            orderIndex: 0
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this course?',
            content: 'This action cannot be undone.',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        { title: 'Title', dataIndex: 'title', key: 'title' },
        { title: 'Slug', dataIndex: 'slug', key: 'slug' },
        { title: 'Order', dataIndex: 'orderIndex', key: 'orderIndex' },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => isActive ? <span style={{ color: 'green' }}>Active</span> : <span style={{ color: 'red' }}>Inactive</span>
        },
        {
            title: 'Linked Form',
            dataIndex: 'formDefinitionId',
            key: 'formDefinitionId',
            render: (formId: string) => {
                if (!formId) return <span style={{ color: '#ccc' }}>None</span>;
                const formObj = forms?.data?.find((f: any) => f.id.toLowerCase() === formId.toLowerCase());
                return formObj ? <Tag color="blue">{formObj.name}</Tag> : <Tag>Custom</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: AdminCourseDto) => (
                <Space size="middle">
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: lang.toUpperCase(),
        children: (
            <>
                <Form.Item name={`translations_${lang}_title`} label="Course Title">
                    <Input placeholder={`Title in ${lang.toUpperCase()}`} />
                </Form.Item>
                <Form.Item name={`translations_${lang}_description`} label="Course Description">
                    <Input.TextArea rows={4} placeholder={`Description in ${lang.toUpperCase()}`} />
                </Form.Item>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <Form.Item name={`translations_${lang}_scheduleText`} label="Schedule (e.g. Mon 18:00)">
                        <Input placeholder={`Schedule in ${lang.toUpperCase()}`} />
                    </Form.Item>
                    <Form.Item name={`translations_${lang}_instructorText`} label="Instructor">
                        <Input placeholder={`Instructor in ${lang.toUpperCase()}`} />
                    </Form.Item>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <Form.Item name={`translations_${lang}_targetGroupText`} label="Target Group">
                        <Input placeholder={`Target Audience in ${lang.toUpperCase()}`} />
                    </Form.Item>
                    <Form.Item name={`translations_${lang}_locationText`} label="Specific Location">
                        <Input placeholder={`Location in ${lang.toUpperCase()}`} />
                    </Form.Item>
                </div>
            </>
        )
    }));

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>Courses Management</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    Create Course
                </Button>
            </div>

            <Table
                dataSource={courses}
                columns={columns}
                rowKey="id"
                loading={isLoading}
            />

            <Modal
                title={editingCourseId ? 'Edit Course' : 'Create Course'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={saveMutation.isPending}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
                    <Tabs type="card" items={tabItems} style={{ marginBottom: 24 }} />

                    <Form.Item name="slug" label="URL Slug (e.g., kalligraphie-kurs)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item label="Image URL (For Course Card)">
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="imageUrl" noStyle>
                                <Input placeholder="e.g. /api/media/download/123e4567..." />
                            </Form.Item>
                            <Upload
                                beforeUpload={handleImageUpload}
                                showUploadList={false}
                                maxCount={1}
                            >
                                <Button icon={<UploadOutlined />} loading={uploadImageMutation.isPending}>
                                    Upload
                                </Button>
                            </Upload>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item name="icon" label="Icon Name (Ant Design)">
                        <Input placeholder="e.g. EditOutlined" />
                    </Form.Item>

                    <Space orientation="vertical" style={{ width: '100%' }} size="large">
                        <Space style={{ display: 'flex', gap: '24px' }}>
                            <Form.Item name="color" label="Card Background Color">
                                <ColorPicker showText />
                            </Form.Item>
                            <Form.Item name="orderIndex" label="Order Index">
                                <InputNumber min={0} />
                            </Form.Item>
                            <Form.Item name="isActive" label="Is Active" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Space>

                        <Space style={{ display: 'flex', gap: '24px', width: '100%' }}>
                            <Form.Item name="dateRange" label="Course Date Range" style={{ flex: 2 }}>
                                <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="price" label="Price (€)" style={{ flex: 1 }}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Space>

                        <Form.Item name="registrationUrl" label="Registration Form URL / PDF Path">
                            <Input placeholder="e.g. /media/public/documents/form.pdf or external link" />
                        </Form.Item>

                        <Form.Item name="formDefinitionId" label="Linked Custom Form (Overrides Registration URL if set)">
                            <Select placeholder="Select a form" allowClear>
                                {forms?.data?.map((f: any) => (
                                    <Select.Option key={f.id} value={f.id}>{f.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};
